import { displayDate } from "../../../../utils/displayDate";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    Stack,
    TableBody,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material";
import { debounce } from "lodash";
import { Eye, GripHorizontal, X } from "lucide-react";
import { api } from "../../../../shared/hooks/api";
import DateRangeFilter from "../../../../shared/components/FilterOption/DateRangeFilter";
import { StyledTable, StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import { ContentWrapper } from "../../../../shared/styled/TableStyles";
import { SearchBarWrapper, SearchText } from "../../../../shared/components/SearchBar/Styled";
import SearchIcon from "../../../../shared/svg/SearchIcon";
import { ColumnMapping, downloadExcel } from "../../../../utils/exportUtils";
import { formatNumber } from "../../../../utils/format";
import { getManufacturingOrderCodesForDisplay } from "../../../../utils/manufacturingOrderCode";
import { error } from "../../../../utils/toast";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import { StyledBackdrop } from "../SharedModalStyled";
import { ROLE } from "../../../../data/enums";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import {
    ManufacturingScheduleGroupRow,
    ManufacturingScheduleOrder,
    ManufacturingProductColumn,
    buildProductColumns,
    buildScheduleExportRows,
    buildScheduleGroupRows,
    getOrderName,
} from "./scheduleGrouping";

interface Filters {
    category: string;
    dateRange: [string | null, string | null];
    productSearch: string;
}

const CATEGORY_OPTIONS = [
    { label: "Phuy", value: "Phuy" },
    { label: "Thùng", value: "Thùng" },
    { label: "Cơ Khí Ô Tô", value: "Cơ Khí Ô Tô" },
];

const WORKSHOP_CATEGORY_OPTIONS_BY_ROLE: Partial<Record<ROLE, typeof CATEGORY_OPTIONS>> = {
    [ROLE.FACTORY_MANAGER_ONE]: CATEGORY_OPTIONS.filter((option) => ["Phuy", "Thùng"].includes(option.value)),
    [ROLE.DEPUTY_FACTORY_MANAGER_ONE]: CATEGORY_OPTIONS.filter((option) => ["Phuy", "Thùng"].includes(option.value)),
    [ROLE.FACTORY_MANAGER_TWO]: CATEGORY_OPTIONS.filter((option) => option.value === "Cơ Khí Ô Tô"),
    [ROLE.DEPUTY_FACTORY_MANAGER_TWO]: CATEGORY_OPTIONS.filter((option) => option.value === "Cơ Khí Ô Tô"),
};

const COMPACT_DATE_COL_WIDTH = 96;
const COMPACT_INFO_COL_WIDTH = 188;
const COMPACT_ACTION_COL_WIDTH = 72;
const EXPANDED_DATE_COL_WIDTH = 96;
const EXPANDED_LSX_COL_WIDTH = 150;
const EXPANDED_CUSTOMER_COL_WIDTH = 190;
const EXPANDED_ACTION_COL_WIDTH = 72;
const PRODUCT_COL_WIDTH = 180;
const STICKY_COL_BG_HEADER = "#F2F4FA";
const FROZEN_SHADOW = "2px 0 4px rgba(0,0,0,0.08)";
const FILTER_HEIGHT = 34;
const MODAL_ORDER_COL_WIDTH = 170;
const MODAL_CUSTOMER_COL_WIDTH = 190;
const MODAL_PRODUCT_COL_WIDTH = 140;
const MODAL_TOTAL_COL_WIDTH = 110;
const SCHEDULE_HEADER_HEIGHT = 62;
const SCHEDULE_ROW_HEIGHT = 58;
const COMPACT_LEFT_PANE_WIDTH = COMPACT_DATE_COL_WIDTH + COMPACT_INFO_COL_WIDTH + COMPACT_ACTION_COL_WIDTH;
const EXPANDED_LEFT_PANE_WIDTH =
    EXPANDED_DATE_COL_WIDTH + EXPANDED_LSX_COL_WIDTH + EXPANDED_CUSTOMER_COL_WIDTH + EXPANDED_ACTION_COL_WIDTH;

const rowBg = (idx: number) => (idx % 2 === 0 ? "#FFFFFF" : "#FBFBFB");
const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");

    return `${year}-${month}-${day}`;
};
const getCurrentWeekDateRange = (): [string, string] => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset);
    const sunday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + mondayOffset + 6);

    return [formatDate(monday), formatDate(sunday)];
};
const formatQuantity = (quantity: number) => formatNumber(quantity || 0);
const formatCountLabel = (count: number, singularLabel: string, emptyLabel: string) => (
    count > 0 ? `${count} ${singularLabel}` : emptyLabel
);
const oneLineTextSx = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};
const twoLineTextSx = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
};

const scheduleHeaderCellSx = (width: number, extra: Record<string, any> = {}) => ({
    minWidth: width,
    width,
    height: SCHEDULE_HEADER_HEIGHT,
    p: "6px 8px",
    backgroundColor: STICKY_COL_BG_HEADER,
    verticalAlign: "middle",
    boxSizing: "border-box",
    ...extra,
});

const scheduleBodyCellSx = (width: number, backgroundColor: string, extra: Record<string, any> = {}) => ({
    minWidth: width,
    width,
    height: SCHEDULE_ROW_HEIGHT,
    maxHeight: SCHEDULE_ROW_HEIGHT,
    p: "5px 8px",
    backgroundColor,
    verticalAlign: "middle",
    lineHeight: "18px",
    boxSizing: "border-box",
    overflow: "hidden",
    ...extra,
});

const DraggableScrollBox = ({
    children,
    maxHeight,
    showHint = false,
    sx,
    contentSx,
}: {
    children: React.ReactNode;
    maxHeight?: string;
    showHint?: boolean;
    sx?: Record<string, any>;
    contentSx?: Record<string, any>;
}) => {
    const dragStateRef = useRef({
        isDragging: false,
        startX: 0,
        scrollLeft: 0,
    });
    const [isDragging, setIsDragging] = useState(false);

    const stopDragging = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!dragStateRef.current.isDragging) return;
        dragStateRef.current.isDragging = false;
        setIsDragging(false);
        try {
            event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
            // Pointer capture may already be released by the browser.
        }
    };

    return (
        <Box sx={{ minWidth: 0, ...sx }}>
            {showHint && (
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        mb: "6px",
                        px: "8px",
                        py: "4px",
                        border: "1px solid #D8DEE8",
                        borderRadius: "5px",
                        backgroundColor: "#F8FAFC",
                        color: "#526171",
                        fontFamily: "Noto Sans",
                        fontSize: "12px",
                        lineHeight: "16px",
                    }}
                >
                    <GripHorizontal size={15} strokeWidth={1.8} />
                    Kéo ngang để xem thêm sản phẩm
                </Box>
            )}
            <Box
                onPointerDown={(event) => {
                    if (event.pointerType === "mouse" && event.button !== 0) return;
                    const target = event.target as HTMLElement;
                    if (target.closest("button") || target.closest("a") || target.closest("input")) return;

                    dragStateRef.current = {
                        isDragging: true,
                        startX: event.clientX,
                        scrollLeft: event.currentTarget.scrollLeft,
                    };
                    setIsDragging(true);
                    event.currentTarget.setPointerCapture(event.pointerId);
                }}
                onPointerMove={(event) => {
                    if (!dragStateRef.current.isDragging) return;
                    event.preventDefault();
                    const deltaX = event.clientX - dragStateRef.current.startX;
                    event.currentTarget.scrollLeft = dragStateRef.current.scrollLeft - deltaX;
                }}
                onPointerUp={stopDragging}
                onPointerCancel={stopDragging}
                onPointerLeave={stopDragging}
                sx={{
                    overflowX: "auto",
                    overflowY: maxHeight ? "auto" : "visible",
                    maxHeight,
                    cursor: isDragging ? "grabbing" : "grab",
                    userSelect: isDragging ? "none" : "auto",
                    touchAction: "pan-y",
                    border: "1px solid #E5EAF1",
                    borderRadius: "6px",
                    backgroundColor: "#FFFFFF",
                    "&::-webkit-scrollbar": {
                        height: "14px",
                        width: "14px",
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "#F1F5F9",
                        borderRadius: "8px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#94A3B8",
                        border: "3px solid #F1F5F9",
                        borderRadius: "8px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#64748B",
                    },
                    scrollbarColor: "#94A3B8 #F1F5F9",
                    scrollbarWidth: "thin",
                    ...contentSx,
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

const buildParams = (f: Filters): Record<string, any> => {
    const p: Record<string, any> = {};
    if (f.category) p.product_category = f.category;
    if (f.dateRange[0] && f.dateRange[1]) {
        p.due_date_start = f.dateRange[0];
        p.due_date_end = f.dateRange[1];
    }
    if (f.productSearch.trim()) p.product_name = f.productSearch.trim();
    return p;
};

const buildColumnMapping = (productColumns: { key: string; name: string }[]): ColumnMapping => {
    const columnMapping: ColumnMapping = {
        due_date: "Ngày đến hạn",
        manufacturing_order_count: "Số LSX",
        manufacturing_order_codes: "Danh sách LSX",
        customer_count: "Số khách hàng",
        customer_names: "Danh sách khách hàng",
        order_count: "Số đơn hàng",
        order_names: "Đơn bán hàng",
    };

    productColumns.forEach((product) => {
        columnMapping[product.key] = product.name;
    });

    return columnMapping;
};

const ScheduleGroupDetailModal = ({
    open,
    row,
    onClose,
    productColumns,
}: {
    open: boolean;
    row: ManufacturingScheduleGroupRow | null;
    onClose: () => void;
    productColumns: ManufacturingProductColumn[];
}) => {
    if (!row) return null;

    const rowProductColumns = productColumns.filter((product) => (
        Object.prototype.hasOwnProperty.call(row.productQuantities, product.key)
    ));
    const detailRows = row.orders.map((order) => ({
        key: order.id,
        order,
        quantities: row.orderProductQuantities[order.id] || {},
    }));
    const displayManufacturingOrderCodes = getManufacturingOrderCodesForDisplay(
        row.manufacturing_order_codes,
    );
    const manufacturingOrderSummary = formatCountLabel(
        displayManufacturingOrderCodes.length,
        "LSX",
        "Chưa có LSX",
    );
    const customerSummary = formatCountLabel(row.customer_names.length, "khách hàng", "Chưa có khách hàng");
    const totalQuantity = Object.values(row.productQuantities).reduce((sum, quantity) => sum + Number(quantity || 0), 0);
    const getOrderTotal = (quantities: Record<string, number>) => (
        rowProductColumns.reduce((sum, product) => sum + Number(quantities[product.key] || 0), 0)
    );
    const productLabel = (product: ManufacturingProductColumn) => (
        product.productId ? (
            <StyledNavLink to={`/products/${product.productId}`}>
                {product.name}
            </StyledNavLink>
        ) : (
            <>{product.name}</>
        )
    );
    const metricBoxSx = {
        px: "12px",
        py: "9px",
        border: "1px solid #D8DEE8",
        borderRadius: "6px",
        backgroundColor: "#FFFFFF",
        minWidth: 0,
    };
    const metricLabelSx = {
        color: "#64748B",
        fontSize: "11px",
        fontWeight: 700,
        lineHeight: "14px",
        textTransform: "uppercase",
    };
    const metricValueSx = {
        mt: "3px",
        color: "#111827",
        fontSize: "18px",
        fontWeight: 800,
        lineHeight: "24px",
        fontVariantNumeric: "tabular-nums",
        ...oneLineTextSx,
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "min(1380px, 96vw)",
                    maxHeight: "90vh",
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 24px 56px rgba(15, 23, 42, 0.24)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    fontFamily: "Noto Sans",
                    outline: "none",
                }}
            >
                <Box
                    sx={{
                        px: "22px",
                        py: "16px",
                        borderBottom: "1px solid #E5EAF1",
                        backgroundColor: "#F6F8FB",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "16px",
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                            sx={{
                                fontSize: "20px",
                                fontWeight: 700,
                                lineHeight: "28px",
                                color: "#1F2937",
                            }}
                        >
                            Chi tiết đơn bán hàng
                        </Box>
                        <Box
                            sx={{
                                mt: "6px",
                                color: "#526171",
                                fontSize: "13px",
                                lineHeight: "19px",
                            }}
                        >
                            {displayDate(row.due_date)} · {detailRows.length} đơn hàng · {rowProductColumns.length} sản phẩm
                        </Box>
                    </Box>
                    <IconButton
                        aria-label="Đóng"
                        onClick={onClose}
                        sx={{
                            width: "34px",
                            height: "34px",
                            border: "1px solid #D8DEE8",
                            borderRadius: "6px",
                            backgroundColor: "#FFFFFF",
                            color: "#526171",
                            transition: "background-color 180ms ease, border-color 180ms ease",
                            "&:hover": {
                                backgroundColor: "#EEF2F7",
                                borderColor: "#B8C0CC",
                            },
                        }}
                    >
                        <X size={18} />
                    </IconButton>
                </Box>

                <Box sx={{ p: "16px 22px 22px", overflow: "hidden", minHeight: 0 }}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, minmax(0, 1fr))",
                                md: "repeat(5, minmax(0, 1fr))",
                            },
                            gap: "10px",
                            mb: "12px",
                        }}
                    >
                        <Box sx={{ ...metricBoxSx, borderColor: "#B7D9EE", backgroundColor: "#F7FBFE" }}>
                            <Box sx={{ ...metricLabelSx, color: "#0058A0" }}>Ngày</Box>
                            <Box sx={{ ...metricValueSx, color: "#0058A0" }}>{displayDate(row.due_date)}</Box>
                        </Box>
                        <Tooltip
                            title={
                                displayManufacturingOrderCodes.join(", ") ||
                                "Chưa có LSX"
                            }
                        >
                            <Box
                                sx={metricBoxSx}
                            >
                                <Box sx={metricLabelSx}>Lệnh sản xuất</Box>
                                <Box sx={metricValueSx}>{manufacturingOrderSummary}</Box>
                            </Box>
                        </Tooltip>
                        <Tooltip title={row.customer_names.join(", ") || "Chưa có khách hàng"}>
                            <Box
                                sx={metricBoxSx}
                            >
                                <Box sx={metricLabelSx}>Khách hàng</Box>
                                <Box sx={metricValueSx}>{customerSummary}</Box>
                            </Box>
                        </Tooltip>
                        <Box sx={metricBoxSx}>
                            <Box sx={metricLabelSx}>Đơn hàng</Box>
                            <Box sx={metricValueSx}>{detailRows.length}</Box>
                        </Box>
                        <Box sx={metricBoxSx}>
                            <Box sx={metricLabelSx}>Tổng số lượng</Box>
                            <Box sx={metricValueSx}>{formatQuantity(totalQuantity)}</Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            mb: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "10px",
                            flexWrap: "wrap",
                        }}
                    >
                        <Box
                            sx={{
                                color: "#374151",
                                fontSize: "13px",
                                fontWeight: 700,
                                lineHeight: "18px",
                            }}
                        >
                            Phân bổ sản phẩm theo đơn hàng
                        </Box>
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                px: "8px",
                                py: "4px",
                                border: "1px solid #D8DEE8",
                                borderRadius: "5px",
                                backgroundColor: "#F8FAFC",
                                color: "#526171",
                                fontSize: "12px",
                                lineHeight: "16px",
                            }}
                        >
                            <GripHorizontal size={15} strokeWidth={1.8} />
                            Kéo ngang để xem thêm sản phẩm
                        </Box>
                    </Box>

                    <DraggableScrollBox
                        maxHeight="56vh"
                        contentSx={{
                            borderColor: "#D8DEE8",
                            borderRadius: "7px",
                        }}
                    >
                        <StyledTable stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell
                                        sx={{
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 6,
                                            minWidth: MODAL_ORDER_COL_WIDTH,
                                            width: MODAL_ORDER_COL_WIDTH,
                                            backgroundColor: STICKY_COL_BG_HEADER,
                                            boxShadow: FROZEN_SHADOW,
                                            fontSize: "12px",
                                            fontWeight: 800,
                                        }}
                                    >
                                        Đơn bán hàng
                                    </StyledTableCell>
                                    <StyledTableCell
                                        sx={{
                                            position: "sticky",
                                            left: MODAL_ORDER_COL_WIDTH,
                                            zIndex: 6,
                                            minWidth: MODAL_CUSTOMER_COL_WIDTH,
                                            width: MODAL_CUSTOMER_COL_WIDTH,
                                            backgroundColor: STICKY_COL_BG_HEADER,
                                            boxShadow: FROZEN_SHADOW,
                                            fontSize: "12px",
                                            fontWeight: 800,
                                        }}
                                    >
                                        Khách hàng
                                    </StyledTableCell>
                                    {rowProductColumns.map((product) => (
                                        <StyledTableCell
                                            key={product.key}
                                            align="right"
                                            sx={{
                                                minWidth: MODAL_PRODUCT_COL_WIDTH,
                                                width: MODAL_PRODUCT_COL_WIDTH,
                                                backgroundColor: STICKY_COL_BG_HEADER,
                                                fontSize: "12px",
                                                fontWeight: 800,
                                            }}
                                        >
                                            <Tooltip title={product.productId ? "Mở chi tiết sản phẩm" : product.name}>
                                                <Box
                                                    sx={{
                                                        ml: "auto",
                                                        maxWidth: MODAL_PRODUCT_COL_WIDTH - 16,
                                                        fontSize: "12px",
                                                        lineHeight: "14px",
                                                        wordBreak: "break-word",
                                                        ...twoLineTextSx,
                                                    }}
                                                >
                                                    {productLabel(product)}
                                                </Box>
                                            </Tooltip>
                                        </StyledTableCell>
                                    ))}
                                    <StyledTableCell
                                        align="right"
                                        sx={{
                                            minWidth: MODAL_TOTAL_COL_WIDTH,
                                            width: MODAL_TOTAL_COL_WIDTH,
                                            backgroundColor: STICKY_COL_BG_HEADER,
                                            fontSize: "12px",
                                            fontWeight: 800,
                                        }}
                                    >
                                        Tổng
                                    </StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {detailRows.map(({ key, order, quantities }, idx) => {
                                    const bg = rowBg(idx);
                                    const orderTotal = getOrderTotal(quantities);
                                    return (
                                        <TableRow key={key}>
                                            <StyledTableCell
                                                sx={{
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 2,
                                                    minWidth: MODAL_ORDER_COL_WIDTH,
                                                    width: MODAL_ORDER_COL_WIDTH,
                                                    backgroundColor: bg,
                                                    boxShadow: FROZEN_SHADOW,
                                                    fontSize: "12px",
                                                    fontWeight: 700,
                                                }}
                                            >
                                                <StyledNavLink to={`/product-orders/${order.id}`}>
                                                    {getOrderName(order)}
                                                </StyledNavLink>
                                            </StyledTableCell>
                                            <StyledTableCell
                                                sx={{
                                                    position: "sticky",
                                                    left: MODAL_ORDER_COL_WIDTH,
                                                    zIndex: 2,
                                                    minWidth: MODAL_CUSTOMER_COL_WIDTH,
                                                    width: MODAL_CUSTOMER_COL_WIDTH,
                                                    backgroundColor: bg,
                                                    boxShadow: FROZEN_SHADOW,
                                                    color: "#334155",
                                                    fontSize: "12px",
                                                    fontWeight: 600,
                                                    ...oneLineTextSx,
                                                }}
                                            >
                                                <Tooltip title={order.customer_name || "—"}>
                                                    <Box sx={oneLineTextSx}>{order.customer_name || "—"}</Box>
                                                </Tooltip>
                                            </StyledTableCell>
                                            {rowProductColumns.map((product) => (
                                                <StyledTableCell
                                                    key={product.key}
                                                    align="right"
                                                    sx={{
                                                        minWidth: MODAL_PRODUCT_COL_WIDTH,
                                                        width: MODAL_PRODUCT_COL_WIDTH,
                                                        backgroundColor: bg,
                                                        color: quantities[product.key] > 0 ? "#111827" : "#94A3B8",
                                                        fontWeight: quantities[product.key] > 0 ? 800 : 600,
                                                        fontVariantNumeric: "tabular-nums",
                                                    }}
                                                >
                                                    {formatQuantity(quantities[product.key] || 0)}
                                                </StyledTableCell>
                                            ))}
                                            <StyledTableCell
                                                align="right"
                                                sx={{
                                                    minWidth: MODAL_TOTAL_COL_WIDTH,
                                                    width: MODAL_TOTAL_COL_WIDTH,
                                                    backgroundColor: bg,
                                                    color: "#0058A0",
                                                    fontWeight: 900,
                                                    fontVariantNumeric: "tabular-nums",
                                                }}
                                            >
                                                {formatQuantity(orderTotal)}
                                            </StyledTableCell>
                                        </TableRow>
                                    );
                                })}
                                <TableRow>
                                    <StyledTableCell
                                        sx={{
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 3,
                                            minWidth: MODAL_ORDER_COL_WIDTH,
                                            width: MODAL_ORDER_COL_WIDTH,
                                            backgroundColor: "#EEF6FC",
                                            boxShadow: FROZEN_SHADOW,
                                            color: "#0058A0",
                                            fontSize: "12px",
                                            fontWeight: 900,
                                        }}
                                    >
                                        Tổng ngày
                                    </StyledTableCell>
                                    <StyledTableCell
                                        sx={{
                                            position: "sticky",
                                            left: MODAL_ORDER_COL_WIDTH,
                                            zIndex: 3,
                                            minWidth: MODAL_CUSTOMER_COL_WIDTH,
                                            width: MODAL_CUSTOMER_COL_WIDTH,
                                            backgroundColor: "#EEF6FC",
                                            boxShadow: FROZEN_SHADOW,
                                            color: "#0058A0",
                                            fontSize: "12px",
                                            fontWeight: 800,
                                        }}
                                    >
                                        {detailRows.length} đơn · {row.customer_names.length} khách
                                    </StyledTableCell>
                                    {rowProductColumns.map((product) => (
                                        <StyledTableCell
                                            key={product.key}
                                            align="right"
                                            sx={{
                                                minWidth: MODAL_PRODUCT_COL_WIDTH,
                                                width: MODAL_PRODUCT_COL_WIDTH,
                                                backgroundColor: "#EEF6FC",
                                                color: "#0058A0",
                                                fontWeight: 900,
                                                fontVariantNumeric: "tabular-nums",
                                            }}
                                        >
                                            {formatQuantity(row.productQuantities[product.key] || 0)}
                                        </StyledTableCell>
                                    ))}
                                    <StyledTableCell
                                        align="right"
                                        sx={{
                                            minWidth: MODAL_TOTAL_COL_WIDTH,
                                            width: MODAL_TOTAL_COL_WIDTH,
                                            backgroundColor: "#DDEFFD",
                                            color: "#0058A0",
                                            fontWeight: 900,
                                            fontVariantNumeric: "tabular-nums",
                                        }}
                                    >
                                        {formatQuantity(totalQuantity)}
                                    </StyledTableCell>
                                </TableRow>
                            </TableBody>
                        </StyledTable>
                    </DraggableScrollBox>
                </Box>
            </Box>
        </Modal>
    );
};

const DetailActionButton = ({ onClick }: { onClick: () => void }) => {
    return (
        <Tooltip title="Xem chi tiết">
            <IconButton
                aria-label="Xem chi tiết"
                onClick={onClick}
                sx={{
                    width: "34px",
                    height: "30px",
                    borderRadius: "5px",
                    color: "#0058A0",
                    backgroundColor: "#E6F2FB",
                    border: "1px solid #B7D9EE",
                    "&:hover": {
                        backgroundColor: "#D5ECFA",
                        borderColor: "#7FC0E8",
                    },
                }}
            >
                <Eye size={16} strokeWidth={2} />
            </IconButton>
        </Tooltip>
    );
};

const ManufacturingScheduleContent = () => {
    const {
        user: { role },
    } = React.useContext(AuthenticationContext);
    const categoryOptions = WORKSHOP_CATEGORY_OPTIONS_BY_ROLE[role as ROLE] ?? CATEGORY_OPTIONS;
    const isWorkshopScoped = Boolean(WORKSHOP_CATEGORY_OPTIONS_BY_ROLE[role as ROLE]);

    const initialFilters = useMemo(() => ({
        category: "",
        dateRange: getCurrentWeekDateRange(),
        productSearch: "",
    }), []);
    const [filters, setFilters] = useState<Filters>(initialFilters);
    const [isFocus, setIsFocus] = useState(false);
    const [isScheduleExpanded, setIsScheduleExpanded] = useState(false);
    const [dateResetKey, setDateResetKey] = useState(0);
    const [selectedRow, setSelectedRow] = useState<ManufacturingScheduleGroupRow | null>(null);

    const [{ data, isLoading }, fetchItems] = api.get<ManufacturingScheduleOrder[]>(
        "/product-flow/v2/orders/manufacturing-schedule/",
        buildParams(initialFilters),
    );

    const fetchItemsRef = useRef(fetchItems);
    useEffect(() => {
        fetchItemsRef.current = fetchItems;
    }, [fetchItems]);

    const debouncedFetch = useMemo(
        () => debounce((params: Record<string, any>) => {
            fetchItemsRef.current(params, true);
        }, 300),
        [],
    );

    useEffect(() => () => {
        debouncedFetch.cancel();
    }, [debouncedFetch]);

    const handleCategoryChange = (value: string) => {
        const next = { ...filters, category: value };
        setFilters(next);
        fetchItems(buildParams(next), true);
    };

    const handleDateRangeChange = (value: [string | null, string | null]) => {
        const next = { ...filters, dateRange: value };
        setFilters(next);
        fetchItems(buildParams(next), true);
    };

    const handleProductSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const next = { ...filters, productSearch: value };
        setFilters(next);
        debouncedFetch(buildParams(next));
    };

    const handleReset = () => {
        const empty: Filters = { category: "", dateRange: [null, null], productSearch: "" };
        setFilters(empty);
        setDateResetKey((k) => k + 1);
        fetchItems({}, true);
    };

    const orders: ManufacturingScheduleOrder[] = Array.isArray(data) ? data : ((data as any)?.results ?? []);
    const scheduleRows = buildScheduleGroupRows(orders);
    const productColumns = buildProductColumns(scheduleRows);
    const leftPaneWidth = isScheduleExpanded ? EXPANDED_LEFT_PANE_WIDTH : COMPACT_LEFT_PANE_WIDTH;

    const handleExport = () => {
        if (scheduleRows.length === 0) {
            error("Không có dữ liệu để xuất trong phạm vi đã chọn", undefined as any);
            return;
        }

        const rows = buildScheduleExportRows(scheduleRows, productColumns);
        const filenameParts = ["lich_san_xuat"];
        if (filters.category) filenameParts.push(filters.category.replace(/\s+/g, "_"));
        if (filters.dateRange[0] && filters.dateRange[1]) {
            filenameParts.push(filters.dateRange[0]!, filters.dateRange[1]!);
        }
        downloadExcel(rows, buildColumnMapping(productColumns), "Lich San Xuat", filenameParts.join("_"), false);
    };

    return (
        <ContentWrapper>
            <Stack
                direction="row"
                spacing={1.5}
                sx={{
                    p: "8px 0",
                    flexWrap: "wrap",
                    alignItems: "center",
                    "& > *": {
                        height: `${FILTER_HEIGHT}px`,
                    },
                }}
            >
                <SearchBarWrapper
                    isFocus={isFocus}
                    style={{
                        width: "260px",
                        minWidth: "200px",
                        height: `${FILTER_HEIGHT}px`,
                        display: "flex",
                        alignItems: "center",
                        borderColor: isFocus ? undefined : "rgba(0, 0, 0, 0.23)",
                    }}
                >
                    <SearchIcon />
                    <SearchText
                        placeholder="Tìm theo sản phẩm hoặc LSX"
                        value={filters.productSearch}
                        onChange={handleProductSearchChange}
                        onFocus={() => setIsFocus(true)}
                        onBlur={() => setIsFocus(false)}
                        spellCheck={false}
                        style={{ fontSize: "14px", fontFamily: "Noto Sans" }}
                    />
                </SearchBarWrapper>

                <FormControl
                    size="small"
                    sx={{
                        minWidth: 180,
                        height: `${FILTER_HEIGHT}px`,
                        "& .MuiInputBase-root": {
                            height: `${FILTER_HEIGHT}px`,
                            borderRadius: "5px",
                            fontFamily: "Noto Sans",
                            fontSize: "14px",
                            alignItems: "center",
                        },
                        "& .MuiSelect-select": {
                            height: `${FILTER_HEIGHT}px`,
                            minHeight: `${FILTER_HEIGHT}px !important`,
                            display: "flex",
                            alignItems: "center",
                            py: 0,
                            pl: "14px",
                            pr: "32px",
                            boxSizing: "border-box",
                            fontFamily: "Noto Sans",
                            fontSize: "14px",
                        },
                        "& .MuiInputLabel-root": {
                            fontFamily: "Noto Sans",
                            fontSize: "14px",
                            lineHeight: "1",
                            transform: "translate(14px, 10px) scale(1)",
                        },
                        "& .MuiInputLabel-shrink": {
                            transform: "translate(14px, -7px) scale(0.75)",
                        },
                    }}
                >
                    <InputLabel>Loại sản phẩm</InputLabel>
                    <Select
                        value={filters.category}
                        label="Loại sản phẩm"
                        onChange={(e) => handleCategoryChange(e.target.value as string)}
                    >
                        <MenuItem value="" sx={{ fontFamily: "Noto Sans", fontSize: "14px" }}>
                            <em>{isWorkshopScoped ? "Tất cả trong xưởng" : "Tất cả"}</em>
                        </MenuItem>
                        {categoryOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily: "Noto Sans", fontSize: "14px" }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Box
                    sx={{
                        minWidth: 260,
                        height: `${FILTER_HEIGHT}px`,
                        "&& .MuiInputBase-root": {
                            height: `${FILTER_HEIGHT}px`,
                            fontFamily: "Noto Sans",
                            fontSize: "14px",
                            borderRadius: "5px",
                            alignItems: "center",
                        },
                        "&& input": {
                            height: `${FILTER_HEIGHT}px`,
                            boxSizing: "border-box",
                            py: 0,
                            fontFamily: "Noto Sans",
                            fontSize: "14px",
                        },
                        "&& fieldset": {
                            borderColor: "rgba(0, 0, 0, 0.23)",
                        },
                        "&&:hover fieldset": {
                            borderColor: "rgba(0, 0, 0, 0.87)",
                        },
                    }}
                >
                    <DateRangeFilter
                        key={dateResetKey}
                        onChangeValue={handleDateRangeChange}
                        initialValue={filters.dateRange}
                        isReset={false}
                    />
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleReset}
                    sx={{
                        height: `${FILTER_HEIGHT}px`,
                        fontFamily: "Noto Sans",
                        fontSize: "13px",
                        textTransform: "none",
                        borderRadius: "5px",
                        borderColor: "#828586",
                        color: "#828586",
                        "&:hover": { borderColor: "#0058A0", color: "#0058A0" },
                        whiteSpace: "nowrap",
                    }}
                >
                    Xóa bộ lọc
                </Button>

                <Button
                    variant="contained"
                    size="small"
                    onClick={handleExport}
                    sx={{
                        height: `${FILTER_HEIGHT}px`,
                        fontFamily: "Noto Sans",
                        fontSize: "13px",
                        textTransform: "none",
                        borderRadius: "5px",
                        backgroundColor: "#0058A0",
                        "&:hover": { backgroundColor: "#0071C5" },
                        whiteSpace: "nowrap",
                    }}
                >
                    Xuất Excel
                </Button>
            </Stack>

            {isLoading ? (
                <div style={{ padding: "24px", fontFamily: "Noto Sans", fontSize: "14px", color: "#828586" }}>
                    Đang tải dữ liệu...
                </div>
            ) : scheduleRows.length === 0 ? (
                <div style={{ padding: "24px", fontFamily: "Noto Sans", fontSize: "14px", color: "#828586" }}>
                    Không có dữ liệu lịch sản xuất.
                </div>
            ) : (
                <Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                            mb: "6px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                px: "8px",
                                py: "4px",
                                border: "1px solid #D8DEE8",
                                borderRadius: "5px",
                                backgroundColor: "#F8FAFC",
                                color: "#526171",
                                fontFamily: "Noto Sans",
                                fontSize: "12px",
                                lineHeight: "16px",
                            }}
                        >
                            <GripHorizontal size={15} strokeWidth={1.8} />
                            Kéo ngang để xem thêm sản phẩm
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setIsScheduleExpanded((previous) => !previous)}
                            sx={{
                                height: 28,
                                minWidth: 92,
                                px: "10px",
                                fontFamily: "Noto Sans",
                                fontSize: "12px",
                                lineHeight: "16px",
                                textTransform: "none",
                                borderRadius: "5px",
                                borderColor: "#B7D9EE",
                                color: "#0058A0",
                                backgroundColor: "#F7FBFE",
                                whiteSpace: "nowrap",
                                "&:hover": {
                                    borderColor: "#7FC0E8",
                                    backgroundColor: "#E6F2FB",
                                },
                            }}
                        >
                            {isScheduleExpanded ? "Thu gọn" : "Mở rộng"}
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: `${leftPaneWidth}px minmax(0, 1fr)`,
                            border: "1px solid #E5EAF1",
                            borderRadius: "6px",
                            overflow: "hidden",
                            backgroundColor: "#FFFFFF",
                        }}
                    >
                        <Box
                            sx={{
                                width: leftPaneWidth,
                                minWidth: leftPaneWidth,
                                overflow: "hidden",
                                backgroundColor: "#FFFFFF",
                                boxShadow: FROZEN_SHADOW,
                                zIndex: 2,
                            }}
                        >
                            <StyledTable stickyHeader style={{ tableLayout: "fixed", width: leftPaneWidth }}>
                                <TableHead>
                                    <TableRow sx={{ height: SCHEDULE_HEADER_HEIGHT }}>
                                        {isScheduleExpanded ? (
                                            <>
                                                <StyledTableCell sx={scheduleHeaderCellSx(EXPANDED_DATE_COL_WIDTH)}>
                                                    Ngày
                                                </StyledTableCell>
                                                <StyledTableCell sx={scheduleHeaderCellSx(EXPANDED_LSX_COL_WIDTH)}>
                                                    LSX
                                                </StyledTableCell>
                                                <StyledTableCell sx={scheduleHeaderCellSx(EXPANDED_CUSTOMER_COL_WIDTH)}>
                                                    Khách hàng
                                                </StyledTableCell>
                                                <StyledTableCell
                                                    align="center"
                                                    sx={scheduleHeaderCellSx(EXPANDED_ACTION_COL_WIDTH)}
                                                >
                                                    Chi tiết
                                                </StyledTableCell>
                                            </>
                                        ) : (
                                            <>
                                                <StyledTableCell sx={scheduleHeaderCellSx(COMPACT_DATE_COL_WIDTH)}>
                                                    Ngày
                                                </StyledTableCell>
                                                <StyledTableCell sx={scheduleHeaderCellSx(COMPACT_INFO_COL_WIDTH)}>
                                                    LSX / Khách
                                                </StyledTableCell>
                                                <StyledTableCell
                                                    align="center"
                                                    sx={scheduleHeaderCellSx(COMPACT_ACTION_COL_WIDTH)}
                                                >
                                                    Chi tiết
                                                </StyledTableCell>
                                            </>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {scheduleRows.map((row, idx) => {
                                        const bg = rowBg(idx);
                                        const displayManufacturingOrderCodes = getManufacturingOrderCodesForDisplay(
                                            row.manufacturing_order_codes,
                                        );
                                        const hasManufacturingOrderCodes = displayManufacturingOrderCodes.length > 0;
                                        const manufacturingOrderText = formatCountLabel(
                                            displayManufacturingOrderCodes.length,
                                            "LSX",
                                            "Chưa có LSX",
                                        );
                                        const manufacturingOrderTooltip = displayManufacturingOrderCodes.length > 0
                                            ? displayManufacturingOrderCodes.join(", ")
                                            : "Chưa có LSX";
                                        const customerText = formatCountLabel(
                                            row.customer_names.length,
                                            "khách hàng",
                                            "Chưa có khách hàng",
                                        );
                                        const customerTooltip = row.customer_names.join(", ") || "Chưa có khách hàng";

                                        return (
                                            <TableRow key={row.key} sx={{ height: SCHEDULE_ROW_HEIGHT }}>
                                                {isScheduleExpanded ? (
                                                    <>
                                                        <StyledTableCell
                                                            sx={{
                                                                ...scheduleBodyCellSx(EXPANDED_DATE_COL_WIDTH, bg),
                                                                fontWeight: 700,
                                                                ...oneLineTextSx,
                                                            }}
                                                        >
                                                            {displayDate(row.due_date)}
                                                        </StyledTableCell>
                                                        <StyledTableCell sx={scheduleBodyCellSx(EXPANDED_LSX_COL_WIDTH, bg)}>
                                                            <Tooltip title={manufacturingOrderTooltip}>
                                                                <Box
                                                                    sx={{
                                                                        color: hasManufacturingOrderCodes ? "#0058A0" : "#828586",
                                                                        fontWeight: hasManufacturingOrderCodes ? 700 : 600,
                                                                        fontSize: "12px",
                                                                        lineHeight: "18px",
                                                                        ...oneLineTextSx,
                                                                    }}
                                                                >
                                                                    {manufacturingOrderText}
                                                                </Box>
                                                            </Tooltip>
                                                        </StyledTableCell>
                                                        <StyledTableCell sx={scheduleBodyCellSx(EXPANDED_CUSTOMER_COL_WIDTH, bg)}>
                                                            <Tooltip title={customerTooltip}>
                                                                <Box
                                                                    sx={{
                                                                        color: "#111827",
                                                                        fontSize: "12px",
                                                                        fontWeight: 600,
                                                                        lineHeight: "18px",
                                                                        ...oneLineTextSx,
                                                                    }}
                                                                >
                                                                    {customerText}
                                                                </Box>
                                                            </Tooltip>
                                                        </StyledTableCell>
                                                        <StyledTableCell
                                                            align="center"
                                                            sx={scheduleBodyCellSx(EXPANDED_ACTION_COL_WIDTH, bg)}
                                                        >
                                                            <DetailActionButton onClick={() => setSelectedRow(row)} />
                                                        </StyledTableCell>
                                                    </>
                                                ) : (
                                                    <>
                                                        <StyledTableCell
                                                            sx={{
                                                                ...scheduleBodyCellSx(COMPACT_DATE_COL_WIDTH, bg),
                                                                fontWeight: 700,
                                                                ...oneLineTextSx,
                                                            }}
                                                        >
                                                            {displayDate(row.due_date)}
                                                        </StyledTableCell>
                                                        <StyledTableCell sx={scheduleBodyCellSx(COMPACT_INFO_COL_WIDTH, bg)}>
                                                            <Tooltip title={manufacturingOrderTooltip}>
                                                                <Box
                                                                    sx={{
                                                                        color: hasManufacturingOrderCodes ? "#0058A0" : "#828586",
                                                                        fontWeight: hasManufacturingOrderCodes ? 700 : 600,
                                                                        fontSize: "12px",
                                                                        lineHeight: "16px",
                                                                        ...oneLineTextSx,
                                                                    }}
                                                                >
                                                                    {manufacturingOrderText}
                                                                </Box>
                                                            </Tooltip>
                                                            <Tooltip title={customerTooltip}>
                                                                <Box
                                                                    sx={{
                                                                        mt: "2px",
                                                                        color: "#111827",
                                                                        fontSize: "12px",
                                                                        fontWeight: 600,
                                                                        lineHeight: "16px",
                                                                        ...oneLineTextSx,
                                                                    }}
                                                                >
                                                                    {customerText}
                                                                </Box>
                                                            </Tooltip>
                                                        </StyledTableCell>
                                                        <StyledTableCell
                                                            align="center"
                                                            sx={scheduleBodyCellSx(COMPACT_ACTION_COL_WIDTH, bg)}
                                                        >
                                                            <DetailActionButton onClick={() => setSelectedRow(row)} />
                                                        </StyledTableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </StyledTable>
                        </Box>

                        <DraggableScrollBox
                            sx={{ minWidth: 0 }}
                            contentSx={{
                                border: "none",
                                borderRadius: 0,
                                height: "100%",
                            }}
                        >
                            <StyledTable
                                stickyHeader
                                style={{
                                    tableLayout: "fixed",
                                    minWidth: Math.max(productColumns.length * PRODUCT_COL_WIDTH, PRODUCT_COL_WIDTH),
                                }}
                            >
                                <TableHead>
                                    <TableRow sx={{ height: SCHEDULE_HEADER_HEIGHT }}>
                                        {productColumns.map((product) => (
                                            <StyledTableCell
                                                key={product.key}
                                                align="right"
                                                sx={scheduleHeaderCellSx(PRODUCT_COL_WIDTH)}
                                            >
                                                <Tooltip title={product.name}>
                                                    <Box
                                                        sx={{
                                                            ml: "auto",
                                                            maxWidth: PRODUCT_COL_WIDTH - 16,
                                                            fontSize: "12px",
                                                            lineHeight: "14px",
                                                            wordBreak: "break-word",
                                                            ...twoLineTextSx,
                                                        }}
                                                    >
                                                        {product.name}
                                                    </Box>
                                                </Tooltip>
                                            </StyledTableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {scheduleRows.map((row, idx) => {
                                        const bg = rowBg(idx);

                                        return (
                                            <TableRow key={row.key} sx={{ height: SCHEDULE_ROW_HEIGHT }}>
                                                {productColumns.map((product) => {
                                                    const quantity = row.productQuantities[product.key] || 0;

                                                    return (
                                                        <StyledTableCell
                                                            key={product.key}
                                                            align="right"
                                                            sx={{
                                                                ...scheduleBodyCellSx(PRODUCT_COL_WIDTH, bg),
                                                                color: quantity > 0 ? "#0D0C0C" : "#94A3B8",
                                                                fontWeight: quantity > 0 ? 800 : 600,
                                                            }}
                                                        >
                                                            {formatQuantity(quantity)}
                                                        </StyledTableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </StyledTable>
                        </DraggableScrollBox>
                    </Box>
                </Box>
            )}

            <ScheduleGroupDetailModal
                open={!!selectedRow}
                row={selectedRow}
                onClose={() => setSelectedRow(null)}
                productColumns={productColumns}
            />
        </ContentWrapper>
    );
};

export default ManufacturingScheduleContent;
